import React from 'react'

import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { GetCreditApplicationDetails_creditApplication_CreditApplication } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'
import { URL } from '@sputnik/resources/constants'
import { logInfo } from '@sputnik/utils/loggerUtils'

export interface AccountCreatedBannerProps
  extends Pick<GetCreditApplicationDetails_creditApplication_CreditApplication, 'authorizedSigner'> {
  salesforceCreditId: string
  hasCreatedPassword: boolean
}
// this banner will show after account is built. The login will always show if it's built and after migration success
const AccountCreatedBanner = ({
  salesforceCreditId,
  authorizedSigner,
  hasCreatedPassword,
}: AccountCreatedBannerProps) => {
  const { isMigration, isLoadingCreditAppTypes } = useGetCreditAppTypes({ salesforceCreditId })
  const authSignerInvitationToken = authorizedSigner?.accountSetupInvitation?.token

  const needsToSetupAccount = authSignerInvitationToken !== null && !hasCreatedPassword && !isMigration

  const navigateToSELogin = () => {
    logInfo('goToSpendAndExpense', {
      attributes: {
        creditAppId: salesforceCreditId,
        result: `Sending user to path: ${URL.DIVVY_PROD}`,
      },
      eventName: 'navigateToSELogin',
    })
    window.open(URL.DIVVY_PROD, '_blank', 'noopener, noreferrer')
  }

  const navigateToSEAccountSetup = () => {
    logInfo('goToSpendAndExpense', {
      attributes: {
        creditAppId: salesforceCreditId,
        result: `Sending user to path: ${URL.DIVVY_PROD_PASSWORD_SETUP}/${authSignerInvitationToken}`,
      },
      eventName: 'navigateToSEAccountSetup',
    })
    window.open(`${URL.DIVVY_PROD_PASSWORD_SETUP}/${authSignerInvitationToken}`, '_blank', 'noopener, noreferrer')
  }

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Success}
        subtext={
          needsToSetupAccount ? (
            <FormattedMessage
              defaultMessage='Now let’s set up your username and password. <link>Set up login</link>'
              id='sputnik.AccountCreatedBanner__dezVS5'
              values={{
                link: (chunks) => (
                  <TextLink
                    disabled={isLoadingCreditAppTypes}
                    size={TextLinkSizes.SMALL}
                    onClick={navigateToSEAccountSetup}
                  >
                    {chunks}
                  </TextLink>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage='You’re all set to get started. <link>Log in</link>'
              id='sputnik.AccountCreatedBanner__hX1g8h'
              values={{
                link: (chunks) => (
                  <TextLink
                    disabled={isLoadingCreditAppTypes}
                    size={TextLinkSizes.SMALL}
                    onClick={navigateToSELogin}
                  >
                    {chunks}
                  </TextLink>
                ),
              }}
            />
          )
        }
        title={
          <FormattedMessage
            defaultMessage='Account created'
            id='sputnik.AccountCreatedBanner__1JRfgv'
          />
        }
      />
    </div>
  )
}

export default AccountCreatedBanner
